"use client";

import React, { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { PORTAL_THEME } from "../../lib/portal_theme";
import Spinner from "../components/Spinner";

const T = PORTAL_THEME;

function SignInContent() {
  const searchParams  = useSearchParams();
  const returnTo      = searchParams.get("returnTo") || "https://app.recruitgraphics.com";
  // Set by "Save Design" (see design/adjust/page.jsx) — saving only means
  // something if the design ends up owned by a real account, so the guest
  // escape hatch that would defeat the whole point is hidden here.
  const noGuest        = searchParams.get("noGuest") === "1";
  const [guestLoading, setGuestLoading] = useState(false);
  const [visible,      setVisible]      = useState(false);

  useEffect(() => {
    // Trigger entrance animation after mount
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res  = await fetch("/api/customer/me", { cache: "no-store" });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.authenticated && !data?.guest) window.location.href = returnTo;
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [returnTo]);

  const handleContinueAsGuest = async () => {
    setGuestLoading(true);
    try {
      const res = await fetch("/api/guest/start", { method: "POST", credentials: "include" });
      if (res.ok) { window.location.href = returnTo; }
    } finally { setGuestLoading(false); }
  };

  const authStartUrl  = `/api/customer/auth/start?returnTo=${encodeURIComponent(returnTo)}`;
  const authSignUpUrl = `/api/customer/auth/start?returnTo=${encodeURIComponent(returnTo)}`;

  const primaryButton = {
    display: "block", padding: "13px 24px", borderRadius: 0,
    background: T.chromeGradient, color: T.chromeButtonText, boxShadow: T.chromeButtonShadow, border: T.chromeBorder,
    fontWeight: 700, fontFamily: T.fontFamily, fontSize: 14,
    textDecoration: "none", textAlign: "center",
    textTransform: "uppercase", letterSpacing: "1px", marginBottom: 10,
  };
  const secondaryButton = {
    display: "block", padding: "12px 24px", borderRadius: 0,
    border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text,
    fontWeight: 700, fontFamily: T.fontFamily, fontSize: 14,
    textDecoration: "none", textAlign: "center",
    textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10,
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "flex-start", justifyContent: "center",
      fontFamily: T.fontFamily, color: T.text, padding: "80px 24px 24px",
    }}>

      {/* Card */}
      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: 380, padding: "36px 32px",
        background: T.cardBackground, border: T.cardEdge, boxShadow: "none", borderRadius: 0,
        opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)",
        overflow: "hidden",
      }}>
        {/* Brand header */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <h1 style={{ fontSize: "clamp(1.4rem, 3vw, 1.8rem)", fontWeight: T.headingWeight, margin: "0 0 8px", letterSpacing: "-0.5px", fontFamily: T.fontFamilyHeadings, textTransform: "uppercase" }}>
            Get Started
          </h1>
        </div>

        {/* Guests are the default way in: an account only adds saved designs,
            so it's offered second. Save Design (noGuest) keeps Sign In first. */}
        {!noGuest && (
          <>
            <button
              type="button"
              disabled={guestLoading}
              onClick={handleContinueAsGuest}
              style={{ ...primaryButton, width: "100%", border: T.chromeBorder, cursor: guestLoading ? "not-allowed" : "pointer", opacity: guestLoading ? 0.6 : 1 }}
            >
              {guestLoading ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Spinner size={14} />Starting…</span> : "Start Designing →"}
            </button>
            <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", margin: "0 0 4px" }}>No account needed</p>

            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0 14px" }}>
              <div style={{ flex: 1, height: 1, background: T.cardBorder }} />
              <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: "0.5px" }}>or save your designs to an account</span>
              <div style={{ flex: 1, height: 1, background: T.cardBorder }} />
            </div>
          </>
        )}

        <a href={authStartUrl} style={noGuest ? primaryButton : secondaryButton}>
          Sign In →
        </a>

        <a href={authSignUpUrl} style={{ ...secondaryButton, marginBottom: 0 }}>
          Create Account
        </a>
      </div>
    </div>
  );
}

export default function SignInPage() {
  return (
    <Suspense fallback={
      <div style={{ minHeight: "100vh", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.fontFamily }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}><Spinner size={18} /><p style={{ color: T.textMuted, fontSize: 13, margin: 0 }}>Loading…</p></div>
      </div>
    }>
      <SignInContent />
    </Suspense>
  );
}
